import React from 'react';
import { Enums } from '@letsdothis/tracking';
import { useStyletron } from 'baseui';
import { HighFive as HighFiveIcon } from '@letsdothis/lds/lib/experimental/Icon';

import { ActivityType } from '@/types/user';
import pluralize from '@/utils/pluralize';

import useHighFive from '../../hooks/useHighFive';
import { Activity } from '../../types';
import Button from '../Button';

export type HighFiveOptions = {
  contentCapturedContentType: Enums.ContentCapturedContentType;
  onHighFive?: () => void;
  onRemoveHighFive?: () => void;
};

export interface HighFiveProps extends HighFiveOptions {
  activity: Activity;
  activityType: ActivityType;
  index?: number;
  eventId: string;
  loginReturnUrl?: string;
  showHighFives?: boolean;
}

const HighFive: React.FC<HighFiveProps> = (props: HighFiveProps) => {
  const [css, theme] = useStyletron();
  const {
    activity,
    activityType,
    eventId,
    contentCapturedContentType,
    onHighFive = () => {},
    onRemoveHighFive = () => {},
    loginReturnUrl,
    showHighFives = false,
  } = props;

  const [
    { toggleHighFive, previouslyHighFived, highFives },
    { loading },
  ] = useHighFive({
    activity,
    eventId,
    activityType,
    contentCapturedContentType,
    loginReturnUrl,
  });
  const hasHighFives = !!highFives.length;

  const onClick = async () => {
    (!previouslyHighFived ? onHighFive : onRemoveHighFive)();
    await toggleHighFive();
  };
  const detailedHighFives = hasHighFives && showHighFives;
  const buttonText = previouslyHighFived ? 'Thanks!' : 'High-Five';

  return (
    <div className={css({ display: 'flex', alignItems: 'center' })}>
      <Button
        disabled={loading}
        onClick={onClick}
        active={previouslyHighFived}
        text={detailedHighFives ? undefined : buttonText}
        Icon={HighFiveIcon}
        title={previouslyHighFived ? 'Remove High-Five' : 'High-Five'}
      />
      {detailedHighFives && (
        <span
          className={css({
            color: theme.colors.mono700,
            lineHeight: '1.375rem',
            fontWeight: 'normal',
            fontSize: '0.875rem',
          })}
        >
          {pluralize('high-five', highFives.length, true)}
        </span>
      )}
    </div>
  );
};

export default HighFive;
